import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { useMe } from "../../hooks/useMe"
import { AppShell } from "../ui/AppShell"
import { ConfirmEmailBanner } from "../Dashboard/ConfirmEmailBanner"
import { Button } from "../ui/Button"
import { PageLoading, Spinner } from "../ui/Spinner"
import { useToast } from "../ui/Toast"
import { PLAN_FROM_TEXT } from "../../lib/pricing"
import { CTA_TRATAMENTO } from "../../lib/narrativa"
import type { AttemptInProgress, ContentMode, ProvaDetailResponse, SimuladoMode } from "../../types"

/**
 * Configurador de simulado (fase B4 — decisão 3.2 do plano): dois eixos,
 * estilo setup de corrida. Só aparece do 2º simulado em diante (o 1º é o
 * nivelamento, Curta direto). Padrão inteligente pré-selecionado (decisão 3.7),
 * escolha sempre disponível como override.
 *
 * Acesso: com plano ativo tudo é liberado. Sem plano, o que é grátis é o
 * treino de uma matéria (Curta + Específico com uma matéria ainda não
 * treinada). O resto mostra o cadeado e aponta pro plano.
 */

const MODE_LABELS: Record<SimuladoMode, { title: string; hint: string }> = {
  curta: { title: "Curta", hint: "Treino rápido do dia" },
  media: { title: "Média", hint: "Sessão de estudo completa" },
  oficial: { title: "Oficial", hint: "Formato da banca, com cronômetro" },
}

const CONTENT_OPTIONS: { key: ContentMode; title: string; hint: string }[] = [
  { key: "completo", title: "Completo", hint: "Todas as matérias, na proporção da prova" },
  { key: "fortalecimento", title: "Fortalecimento", hint: "Monta o treino onde você mais ganha pontos" },
  { key: "especifico", title: "Específico", hint: "Você escolhe as matérias" },
]

function fmtDuration(minutes: number, mode: SimuladoMode): string {
  if (mode !== "oficial") return mode === "curta" ? "~15 min" : "~35 min"
  return `${Math.round(minutes / 60)}h cronometradas`
}

export default function Configurador() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { me } = useMe()

  const [data, setData] = useState<ProvaDetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<SimuladoMode | null>(null)
  const [content, setContent] = useState<ContentMode>("completo")
  const [subjects, setSubjects] = useState<Set<string>>(new Set())
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    api
      .get<ProvaDetailResponse>(`/api/provas/${id}`)
      .then((d) => {
        setData(d)
        // Padrão inteligente: reta final (< 20 dias) com histórico → Oficial;
        // caso contrário → Curta (decisão 3.7 / janelas da seção 4.6)
        const days = d.prova.days_until_exam
        // first_simulado é user-wide; performance.total é só desta prova
        const smart: SimuladoMode =
          days != null && days < 20 && !d.prova.first_simulado ? "oficial" : "curta"
        setMode(d.prova.modes.some((m) => m.mode === smart) ? smart : (d.prova.modes[0]?.mode ?? "oficial"))
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Erro ao carregar"))
  }, [id])

  const selectedMode = useMemo(
    () => data?.prova.modes.find((m) => m.mode === mode) ?? null,
    [data, mode]
  )

  const hasAccess = me?.access.active ?? false
  // Histórico user-wide (qualquer modo/prova) — Fortalecimento pondera por matéria do usuário
  const hasHistory = data != null && !data.prova.first_simulado
  const needsSubjects = content === "especifico" && subjects.size === 0
  const chosen = Array.from(subjects)
  const chosenSubject = chosen.length === 1 ? (data?.prova.subjects.find((s) => s.slug === chosen[0]) ?? null) : null
  // Diagnóstico grátis da matéria: Curta + Específico com exatamente uma
  // matéria ainda não diagnosticada.
  const freeTraining =
    mode === "curta" && content === "especifico" && chosenSubject != null && chosenSubject.free_training_available
  const allowed = hasAccess || freeTraining
  const anyFreeLeft = data?.prova.subjects.some((s) => s.free_training_available) ?? false

  function toggleSubject(slug: string) {
    setSubjects((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  async function start() {
    if (!data || !mode) return
    setStarting(true)
    try {
      const attemptData = await api.post<{ attempt: AttemptInProgress }>("/api/attempts", {
        blueprint_id: data.prova.id,
        mode,
        content_mode: content,
        subjects: content === "especifico" ? Array.from(subjects) : undefined,
      })
      navigate(`/simulado/${attemptData.attempt.id}`)
    } catch (e) {
      if (e instanceof ApiError && e.code === "subscription_required") {
        if (e.body.unconfirmed === true) {
          toast("Confirme seu e-mail pra iniciar o simulado", "info")
        } else {
          toast("Esse formato faz parte do plano de acesso", "info")
          navigate("/plano")
        }
      } else if (e instanceof ApiError && e.code === "attempt_in_progress" && typeof e.body.attempt_id === "string") {
        navigate(`/simulado/${e.body.attempt_id}`)
      } else {
        toast(e instanceof ApiError ? e.message : "Erro ao iniciar o simulado", "error")
      }
    } finally {
      setStarting(false)
    }
  }

  if (error) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error}</p>
          <Link to="/dashboard">
            <Button variant="secondary">Voltar</Button>
          </Link>
        </div>
      </AppShell>
    )
  }

  if (!data || !mode) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <div>
          <Link to={`/prova/${data.prova.id}`} className="text-sm font-medium text-gold-600 hover:text-gold-600">
            ← {data.prova.name}
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-navy-900">Monte seu treino</h1>
          <p className="mt-0.5 text-sm text-navy-500">Escolha o tamanho e o foco do seu simulado.</p>
        </div>

        {/* Eixo 1: tamanho */}
        <section aria-label="Tamanho do simulado">
          <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Tamanho</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {data.prova.modes.map((m) => {
              const active = m.mode === mode
              const label = MODE_LABELS[m.mode]
              return (
                <button
                  key={m.mode}
                  type="button"
                  onClick={() => setMode(m.mode)}
                  aria-pressed={active}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    active
                      ? "border-gold-400 bg-gold-50/70 ring-1 ring-gold-400"
                      : "border-slate-200 bg-white hover:border-navy-300"
                  }`}
                >
                  <p className="font-display text-base font-bold uppercase tracking-wide text-navy-900">
                    {label.title}
                  </p>
                  <p className="mt-1 text-xs text-navy-500">
                    {m.n_questions} questões · {fmtDuration(m.duration_minutes, m.mode)}
                  </p>
                  {!hasAccess && (
                    <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-gold-600">
                      {m.mode === "curta" ? "Diagnóstico grátis · 1 por matéria" : "Requer plano"}
                    </p>
                  )}
                  <p className="mt-0.5 text-[11px] text-navy-400">{label.hint}</p>
                </button>
              )
            })}
          </div>
        </section>

        {/* Eixo 2: conteúdo */}
        <section aria-label="Conteúdo do simulado">
          <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Conteúdo</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {CONTENT_OPTIONS.map((opt) => {
              const active = content === opt.key
              const locked = opt.key === "fortalecimento" && !hasHistory
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={locked}
                  onClick={() => setContent(opt.key)}
                  aria-pressed={active}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    locked
                      ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
                      : active
                        ? "border-gold-400 bg-gold-50/70 ring-1 ring-gold-400"
                        : "border-slate-200 bg-white hover:border-navy-300"
                  }`}
                >
                  <p className="font-display text-base font-bold uppercase tracking-wide text-navy-900">
                    {opt.title}
                  </p>
                  <p className="mt-1 text-xs text-navy-500">
                    {locked ? "Complete um simulado pra desbloquear" : opt.hint}
                  </p>
                  {!hasAccess && !locked && opt.key !== "especifico" && (
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gold-600">Requer plano</p>
                  )}
                </button>
              )
            })}
          </div>

          {content === "especifico" && (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-navy-400">
                Quais matérias?
              </p>
              <div className="flex flex-wrap gap-2">
                {data.prova.subjects.map((s) => {
                  const on = subjects.has(s.slug)
                  const free = !hasAccess && s.free_training_available
                  return (
                    <button
                      key={s.slug}
                      type="button"
                      onClick={() => toggleSubject(s.slug)}
                      aria-pressed={on}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                        on
                          ? "border-navy-800 bg-navy-800 text-white"
                          : "border-slate-300 bg-white text-navy-600 hover:border-navy-400"
                      }`}
                    >
                      {s.name}
                      {free && (
                        <span className={`ml-1.5 text-[10px] font-bold uppercase ${on ? "text-gold-300" : "text-gold-600"}`}>
                          grátis
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              {!hasAccess && (
                <p className="mt-2.5 text-xs text-navy-400">
                  Sem plano: 1 diagnóstico grátis por matéria — Curta, com uma matéria só.
                  {!anyFreeLeft && " Você já usou todos."}
                </p>
              )}
            </div>
          )}
        </section>

        {/* Iniciar */}
        <div>
          {me && !me.confirmed ? (
            // Sem e-mail confirmado nada inicia — o passo está a um clique.
            <ConfirmEmailBanner />
          ) : allowed ? (
            <Button size="lg" block onClick={() => void start()} disabled={starting || needsSubjects}>
              {starting && <Spinner className="h-4 w-4 text-white" />}
              {needsSubjects
                ? "Escolha ao menos uma matéria"
                : freeTraining && !hasAccess
                  ? `Fazer o diagnóstico de ${chosenSubject?.name ?? "matéria"}`
                  : "Iniciar simulado"}
            </Button>
          ) : (
            <Link to="/plano">
              <Button size="lg" block variant="gold">
                {CTA_TRATAMENTO}
              </Button>
            </Link>
          )}
          {!hasAccess && !allowed && me?.confirmed && (
            <p className="mt-2 text-center text-xs text-navy-400">
              {PLAN_FROM_TEXT}. Sem pagar, você ainda tem 1 diagnóstico grátis por matéria: Curta + Específico com uma matéria.
            </p>
          )}
        </div>
      </div>
    </AppShell>
  )
}
